import React, { useState, useRef, useEffect } from "react";
import RowGroup from "./RowGroup";
import ResultLayout from "./ResultLayout";
import TimelinessBreakdown from "./TimelinessBreakdown";
import { SCRUBBER_API_BASE } from "../api/config";
import { csrfFetch } from "../api/csrf";

export default function UniverseViewer({ universe, errors = [], selectedTable, userEmail, submissionId, timelinessSummary, monthlyTimeliness = null, timeliness = null, totalRows = 0, universeMode = 'full', cleanRowsOmitted = 0, serverTotalErrorCount = 0 }) {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [onlyShowErroredRows, setOnlyShowErroredRows] = useState(false);

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [topScrollWidth, setTopScrollWidth] = useState(0);

  const allFields =
    universe && universe.length > 0 ? Object.keys(universe[0].data) : [];

  const categoryCounts =
    universe && universe.length > 0
      ? universe.reduce((acc, row) => {
          Object.values(row.data).forEach((cell) => {
            if (cell.category) {
              acc[cell.category] = (acc[cell.category] || 0) + 1;
            }
          });
          // Row-level issues that never attached to a cell (no matching
          // column) still get their category chip — otherwise whole
          // categories vanish from the view.
          (row.issues || []).forEach((iss) => {
            if (iss?.category && iss?.field && !(iss.field in (row.data || {}))) {
              acc[iss.category] = (acc[iss.category] || 0) + 1;
            }
          });
          return acc;
        }, {})
      : {};

  // Create a comprehensive error count that includes all validation errors
  const totalErrorCount = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
  
  console.log('🛠 DEBUG — Error counts:', {
    errorsArrayLength: errors?.length || 0,
    categoryCounts,
    
    totalErrorCount,
    universeLength: universe?.length || 0
  });

  const categories = ["All", ...Object.keys(categoryCounts)];

  // Keep scroll bars in sync
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableScroll = tableContainerRef.current;
    if (!topScroll || !tableScroll) return;

    const syncScroll = (source, target) => {
      target.scrollLeft = source.scrollLeft;
    };
    const onTopScroll = () => syncScroll(topScroll, tableScroll);
    const onTableScroll = () => syncScroll(tableScroll, topScroll);

    topScroll.addEventListener("scroll", onTopScroll);
    tableScroll.addEventListener("scroll", onTableScroll);

    return () => {
      topScroll.removeEventListener("scroll", onTopScroll);
      tableScroll.removeEventListener("scroll", onTableScroll);
    };
  }, []);

  // Measure table width for the top horizontal scrollbar
  useEffect(() => {
    const measure = () => {
      const table = tableRef.current;
      if (table) {
        const width = table.scrollWidth || table.offsetWidth || 0;
        setTopScrollWidth(width);
      }
    };
    measure();

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // Recalculate when data or filters change
  }, [universe, selectedCategories, onlyShowErroredRows]);

  if (!universe || universe.length === 0) return null;

  return (
    <ResultLayout
      categoryFilters={categories}
      selectedCategories={selectedCategories}
      onCategoryToggle={(cat) => {
        setSelectedCategories((prev) =>
          prev.includes(cat)
            ? prev.filter((c) => c !== cat)
            : [...prev, cat]
        );
      }}
      onlyShowErroredRows={onlyShowErroredRows}
      onToggleErrors={setOnlyShowErroredRows}
      issueCounts={{ ...categoryCounts, "Total Errors": totalErrorCount }}
      universe={universe}
      timeliness={timeliness}
      totalErrorCount={serverTotalErrorCount || totalErrorCount}
      totalRows={totalRows}
      errors={errors}
      selectedTable={selectedTable}
      userEmail={userEmail}
      submissionId={submissionId}
      onExport={async (type) => {
        try {
          console.log('🛠 DEBUG — Export requested with:', {
            type,
            selectedTable,
            errorsCount: errors?.length || 0,
            universeCount: universe?.length || 0
          });
          
          const resp = await csrfFetch(`${SCRUBBER_API_BASE}/api/export`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              format: type,
              errors,
              universe,
              universeMode: universeMode || 'full',
              cleanRowsOmitted: cleanRowsOmitted || 0,
              universeRowCount: totalRows || universe.length,
              filename: selectedTable?.universeType || "grievances_export",
              tableLabel: selectedTable?.label || "Unknown Table",
              timelinessSummary: timelinessSummary || null
            }),
          });

          if (!resp.ok) {
            const text = await resp.text();
            throw new Error(text || `Export failed with ${resp.status}`);
          }

          const blob = await resp.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${selectedTable?.label?.replace(/\s+/g, '_') || 'universe'}_export.${type}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.error(e);
          alert("Export failed. Check console for details.");
        }
      }}
    >
      {/* Per-universe timeliness breakdown — percentages by scenario + general
          timeliness. Renders only when the universe has timeliness scenarios. */}
      {universeMode === 'issues-only' && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Large universe: {totalRows.toLocaleString()} rows validated. Showing the {universe.length.toLocaleString()} row{universe.length === 1 ? '' : 's'} that need review
          {cleanRowsOmitted > 0 ? ` — ${cleanRowsOmitted.toLocaleString()} clean rows passed and are counted but not listed` : ''}.
          All counts and the submission cover every row; exports contain the rows shown here.
        </div>
      )}
      {timelinessSummary?.scenarios?.length > 0 && (
        <div className="mb-6">
          <TimelinessBreakdown timelinessSummary={timelinessSummary} monthlyTimeliness={monthlyTimeliness} />
        </div>
      )}

      {/* Enhanced Header Info */}
      <div className="space-y-6 mb-6">
                 {errors.filter((e) => e.row === "Header").length > 0 && (
           <div className="
             bg-gradient-to-r from-yellow-50/80 via-orange-50/70 to-red-50/60
             border border-yellow-300/50 rounded-xl p-3 shadow-sm shadow-yellow-200/20
             backdrop-blur-sm
           ">
             <div className="flex items-center gap-2 mb-3">
               <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border border-yellow-300/40 rounded-lg">
                 <div className="w-1.5 h-1.5 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-full"></div>
                 <span className="text-xs font-semibold text-yellow-700">Header Issues</span>
               </div>
               <p className="text-sm font-medium text-yellow-800">⚠️ Validation Issues</p>
             </div>
             <div className="flex flex-wrap gap-2">
               {Object.entries(
                 errors
                   .filter((e) => e.row === "Header")
                   .reduce((acc, curr) => {
                     const key = curr.field;
                     if (!acc[key]) acc[key] = [];
                     acc[key].push(curr.message);
                     return acc;
                   }, {})
               ).map(([field, messages], idx) => (
                 <div
                   key={idx}
                   className="
                     border border-yellow-200/50 bg-gradient-to-r from-yellow-100/70 to-orange-100/60
                     rounded-lg px-3 py-2 text-xs text-yellow-900 shadow-sm
                     hover:shadow-md transition-all duration-200
                     flex-shrink-0
                   "
                 >
                   {messages.some((msg) => msg === "Missing required column") ? (
                     <div className="flex items-center gap-2">
                       <div className="w-1.5 h-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full"></div>
                       <span><strong>Missing:</strong> {field}</span>
                     </div>
                   ) : (
                     <div className="flex items-center gap-2">
                       <div className="w-1.5 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                       <span><strong>Incorrect:</strong> {field}</span>
                     </div>
                   )}
                 </div>
               ))}
             </div>
           </div>
         )}
        
        {/* Enhanced Row Counter */}
        <div className="
          inline-flex items-center gap-3 px-6 py-3
          bg-gradient-to-r from-slate-800/90 via-rose-800/80 to-orange-800/70
          rounded-2xl shadow-lg shadow-slate-500/25
          text-white backdrop-blur-sm
          hover:shadow-xl hover:shadow-slate-600/30 transition-all duration-300
        ">
          <div className="w-3 h-3 bg-gradient-to-r from-rose-400 to-orange-500 rounded-full"></div>
          <span className="text-slate-300 font-medium">Showing</span>
          <span className="font-bold text-white text-lg">
            {
              universe.filter((row) => {
                const matchesCategory =
                  selectedCategories.length === 0 ||
                  Object.values(row.data).some((cell) =>
                    selectedCategories.includes(cell.category)
                  ) ||
                  (row.timelinessCategory &&
                    selectedCategories.includes(row.timelinessCategory));
                const hasIssue = Object.values(row.data).some(
                  (cell) => cell.issue !== null
                );
                return (
                  matchesCategory && (!onlyShowErroredRows || hasIssue)
                );
              }).length
            }
          </span>
          <span className="text-slate-300 font-medium">of {universe.length} rows</span>
        </div>
      </div>


      {/* Modern SaaS Table - CENTERED AND SCROLLABLE */}
      <div className="flex justify-center w-full">
        <div
          ref={tableContainerRef}
          className="
            overflow-x-scroll overflow-y-auto max-h-[75vh]
            scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-slate-100
            rounded-xl border border-slate-200/80 
            shadow-2xl shadow-slate-900/10
            bg-white/98 backdrop-blur-xl
            ring-1 ring-slate-900/5
            mx-auto
          "
          style={{ 
            width: '95vw',
            maxWidth: '1400px',
            overflowX: 'scroll', 
            overflowY: 'auto',
            scrollbarWidth: 'thin'
          }}
        >
        <table
          ref={tableRef}
          className="border-collapse text-sm w-full"
          style={{ minWidth: '100%' }}
        >
          <thead className="
            bg-slate-50/80 backdrop-blur-sm border-b border-slate-200/60
            text-slate-700 sticky top-0 z-30 
            shadow-sm shadow-slate-900/5
          ">
            <tr>
              <th className="
                px-6 py-4 text-left font-semibold text-slate-600
                border-r border-slate-200/60 sticky left-0 z-40
                bg-slate-50/90 backdrop-blur-sm
              ">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-rose-400 to-orange-500 rounded-full"></div>
                  Row
                </div>
              </th>
              {selectedCategories.map((category) => (
                <th
                  key={category}
                  className="px-6 py-4 font-semibold text-slate-600 text-left border-r border-slate-200/60 whitespace-nowrap hover:bg-slate-100/50 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <span>{category} Issues</span>
                    <span className="
                      px-3 py-1 rounded-full text-xs bg-gradient-to-r from-red-500 to-pink-600
                      text-white font-bold shadow-sm
                    ">
                      {universe.reduce((acc, row) => {
                        const fieldLevelCount = Object.values(row.data).filter(
                          (cell) => cell.category === category
                        ).length;
                        const issueLevelCount = row.issues
                          ? row.issues.filter(
                              (issue) => issue.category === category
                            ).length
                          : 0;
                        return acc + fieldLevelCount + issueLevelCount;
                      }, 0)}
                    </span>
                  </div>
                </th>
              ))}
              {allFields.map((field, idx) => (
                <th
                  key={idx}
                  className="px-6 py-4 text-left font-semibold text-slate-600 border-r border-slate-200/60 whitespace-nowrap hover:bg-slate-100/50 transition-all duration-200"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      idx % 4 === 0 ? 'bg-gradient-to-r from-orange-400 to-amber-600' :
                      idx % 4 === 1 ? 'bg-gradient-to-r from-amber-400 to-purple-600' :
                      idx % 4 === 2 ? 'bg-gradient-to-r from-purple-400 to-pink-600' :
                      'bg-gradient-to-r from-pink-400 to-red-600'
                    }`}></div>
                    {field}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/60 bg-white">
            {[...Array(Math.ceil(universe.length / 50))].map((_, groupIndex) => {
              const start = groupIndex * 50;
              const end = Math.min(start + 50, universe.length);
              const groupRows = universe.slice(start, end);
              const label = `Rows ${start + 1}–${end}`;
              return (
                <RowGroup
                  key={groupIndex}
                  rows={groupRows}
                  groupLabel={label}
                  allFields={allFields}
                  selectedCategories={selectedCategories}
                  onCategoryToggle={(cat) => {
                    setSelectedCategories((prev) =>
                      prev.includes(cat)
                        ? prev.filter((c) => c !== cat)
                        : [...prev, cat]
                    );
                  }}
                />
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </ResultLayout>
  );
}
